// The recovery code (spec section 6.1): 150 random bits shown as six groups of
// five Crockford base32 characters (`7Q4KX-M2H9P-...`). KEK_r =
// HKDF-SHA256(code, salt, info="hogc-one/courses/v1/recovery"); MK wrapped
// under it with AES-GCM into `{ salt, iv, wrapped }` (API contract section 4),
// bound to the member and the MK generation. High entropy, so no slow KDF is
// needed, and a wrong code fails the GCM tag check rather than being compared
// to anything. The app never stores a code, and never puts what a member typed
// into an error message.
import { hashCanonical } from './canonical';
import { CROCKFORD_ALPHABET, base64ToBytes, bytesToBase64, utf8Encode } from './encoding';
import { assertKeyId, assertMemberId } from './identifiers';
import { MASTER_KEY_BYTES, assertMasterKey } from './master-key';
import { getSubtle, hkdfKey, randomBytes } from './webcrypto';

export const RECOVERY_CODE_GROUPS = 6;
export const RECOVERY_CODE_GROUP_LENGTH = 5;
const CODE_LENGTH = RECOVERY_CODE_GROUPS * RECOVERY_CODE_GROUP_LENGTH;
const SALT_BYTES = 16;
const IV_BYTES = 12;
const KEK_INFO = 'recovery';
const WRAP_AAD_LABEL = 'hogc-one/courses/v1/recovery';

export const RECOVERY_CODE_ERRORS = Object.freeze({
  /** The typed code is not 30 Crockford characters. */
  MALFORMED: 'MALFORMED',
  /** The code is well-formed but does not open this wrapping. */
  WRONG_CODE: 'WRONG_CODE',
  /** The stored wrapping is not `{ salt, iv, wrapped }` in base64. */
  MALFORMED_WRAPPING: 'MALFORMED_WRAPPING',
});

export class RecoveryCodeError extends Error {
  /**
   * @param {keyof typeof RECOVERY_CODE_ERRORS} code
   * @param {string} message
   */
  constructor(code, message) {
    super(message);
    this.name = 'RecoveryCodeError';
    this.code = code;
  }
}

/**
 * @param {string} input
 * @returns {string} the 30 canonical characters, or a MALFORMED error saying
 *   why: case, spaces and separators are forgiven, and O, I and L are read as
 *   0 and 1, the way Crockford intends
 */
export function normaliseRecoveryCode(input) {
  if (typeof input !== 'string') {
    throw new RecoveryCodeError(RECOVERY_CODE_ERRORS.MALFORMED, 'Enter your recovery code');
  }
  const folded = input
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '')
    .replace(/O/g, '0')
    .replace(/[IL]/g, '1');
  if (folded.length !== CODE_LENGTH) {
    throw new RecoveryCodeError(
      RECOVERY_CODE_ERRORS.MALFORMED,
      `A recovery code has ${CODE_LENGTH} characters in ${RECOVERY_CODE_GROUPS} groups of ${RECOVERY_CODE_GROUP_LENGTH}; this one has ${folded.length}`,
    );
  }
  if (Array.from(folded).some(char => !CROCKFORD_ALPHABET.includes(char))) {
    throw new RecoveryCodeError(
      RECOVERY_CODE_ERRORS.MALFORMED,
      'A recovery code never contains the letter U; check it and try again',
    );
  }
  return folded;
}

/**
 * @param {string} normalised 30 canonical characters
 * @returns {string} six hyphenated groups of five, the form shown to the member
 */
export function formatRecoveryCode(normalised) {
  const code = normaliseRecoveryCode(normalised);
  const groups = [];
  for (let i = 0; i < CODE_LENGTH; i += RECOVERY_CODE_GROUP_LENGTH) {
    groups.push(code.slice(i, i + RECOVERY_CODE_GROUP_LENGTH));
  }
  return groups.join('-');
}

/**
 * @returns {string} a fresh formatted code: 30 characters of 5 random bits each
 */
export function generateRecoveryCode() {
  const bytes = randomBytes(CODE_LENGTH);
  return formatRecoveryCode(Array.from(bytes, byte => CROCKFORD_ALPHABET[byte & 31]).join(''));
}

/**
 * KEK_r: HKDF-SHA256 over the canonical code text.
 *
 * @param {string} normalised
 * @param {Uint8Array} salt
 * @param {KeyUsage[]} usages
 * @returns {Promise<CryptoKey>}
 */
function deriveRecoveryKey(normalised, salt, usages) {
  return hkdfKey({
    ikm: utf8Encode(normalised),
    salt,
    info: KEK_INFO,
    algorithm: { name: 'AES-GCM', length: 256 },
    usages,
  });
}

/**
 * @param {number} memberId
 * @param {string} keyId
 * @returns {Uint8Array}
 */
function wrapAad(memberId, keyId) {
  return utf8Encode(`${WRAP_AAD_LABEL}:${memberId}:${keyId}`);
}

/**
 * @param {object} args
 * @param {Uint8Array} args.mk
 * @param {string} args.keyId MK's generation
 * @param {number} args.memberId
 * @param {string} args.code as generated or as typed
 * @returns {Promise<{ salt: string, iv: string, wrapped: string }>} the `recoveryWrapping`
 */
export async function wrapMasterKeyUnderCode({ mk, keyId, memberId, code }) {
  assertMasterKey(mk);
  assertKeyId(keyId);
  assertMemberId(memberId);
  const normalised = normaliseRecoveryCode(code);
  const salt = randomBytes(SALT_BYTES);
  const key = await deriveRecoveryKey(normalised, salt, ['encrypt']);
  const iv = randomBytes(IV_BYTES);
  const wrapped = await getSubtle().encrypt(
    { name: 'AES-GCM', iv, additionalData: wrapAad(memberId, keyId) },
    key,
    mk,
  );
  return {
    salt: bytesToBase64(salt),
    iv: bytesToBase64(iv),
    wrapped: bytesToBase64(new Uint8Array(wrapped)),
  };
}

/**
 * @param {unknown} wrapping
 * @returns {{ salt: Uint8Array, iv: Uint8Array, wrapped: Uint8Array }}
 */
function decodeWrapping(wrapping) {
  const malformed = detail =>
    new RecoveryCodeError(
      RECOVERY_CODE_ERRORS.MALFORMED_WRAPPING,
      `The saved recovery wrapping is not valid: ${detail}`,
    );
  if (wrapping === null || typeof wrapping !== 'object') throw malformed('not an object');
  let salt;
  let iv;
  let wrapped;
  try {
    salt = base64ToBytes(wrapping.salt);
    iv = base64ToBytes(wrapping.iv);
    wrapped = base64ToBytes(wrapping.wrapped);
  } catch (error) {
    throw malformed('salt, iv or wrapped is not base64');
  }
  if (iv.length !== IV_BYTES) throw malformed('iv length');
  if (salt.length === 0) throw malformed('empty salt');
  return { salt, iv, wrapped };
}

/**
 * @param {object} args
 * @param {unknown} args.wrapping `recoveryWrapping` from the status response
 * @param {string} args.keyId the bundle's `keyId` from the same response
 * @param {number} args.memberId
 * @param {string} args.code as typed by the member
 * @returns {Promise<Uint8Array>} MK, or WRONG_CODE
 */
export async function unwrapMasterKeyWithCode({ wrapping, keyId, memberId, code }) {
  assertKeyId(keyId);
  assertMemberId(memberId);
  const normalised = normaliseRecoveryCode(code);
  const { salt, iv, wrapped } = decodeWrapping(wrapping);
  const key = await deriveRecoveryKey(normalised, salt, ['decrypt']);
  let mk;
  try {
    mk = new Uint8Array(
      await getSubtle().decrypt(
        { name: 'AES-GCM', iv, additionalData: wrapAad(memberId, keyId) },
        key,
        wrapped,
      ),
    );
  } catch (error) {
    throw new RecoveryCodeError(
      RECOVERY_CODE_ERRORS.WRONG_CODE,
      'That recovery code does not match. Check it and try again',
    );
  }
  if (mk.length !== MASTER_KEY_BYTES) {
    throw new RecoveryCodeError(
      RECOVERY_CODE_ERRORS.WRONG_CODE,
      'That recovery code does not match',
    );
  }
  return mk;
}

/**
 * The `recoveryWrappingHash` a recovery signature carries (API contract section 5).
 *
 * @param {{ salt: string, iv: string, wrapped: string }} wrapping
 * @returns {Promise<string>}
 */
export function recoveryWrappingHash({ salt, iv, wrapped }) {
  return hashCanonical({ salt, iv, wrapped });
}
